/**
 * 管理員頁面模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from 'react';
import { JSX } from 'react';
import get from 'lodash/get';
import { useRecordContext, usePermissions, Pagination, Labeled, ReferenceArrayField, SingleFieldList, ChipField, ReferenceArrayInput, AutocompleteArrayInput } from 'react-admin';
import { List, Show, TabbedShowLayout, Tab, Datagrid } from 'react-admin';
import { Create, Edit, SimpleForm } from 'react-admin';
import { TextField, DateField, ReferenceManyField, ReferenceField, ShowButton } from 'react-admin';
import { TextInput, PasswordInput, SelectInput } from 'react-admin';
import { ListEditButton, MemberLogDataField, MemberLogTypeField } from '../lib/components';

//-----------------------------------------------------------------------
// defines
import { MEMBER_GROUP, COMMON_THREE_STATUS } from "../common/defines";
import { NamePairInfo } from '../common/interfaces';
import { MemberAuthInfo } from '../providers/authProvider';

//-----------------------------------------------------------------------
/** 管理員群組顯示欄位元件。 */

const memberGroupMap = new Map<number, string>();
const memberGroupList: NamePairInfo<number>[] =
	[
		{ id: MEMBER_GROUP.Owner, name: "擁有者" },
		{ id: MEMBER_GROUP.Manager, name: "管理者" },
		{ id: MEMBER_GROUP.Editor, name: "編輯者" },
		{ id: MEMBER_GROUP.Viewer, name: "檢視者" },
		{ id: MEMBER_GROUP.Guest, name: "訪客" }
	];

for (let group of memberGroupList)
	memberGroupMap.set(group.id, group.name);

export const MemberGroupField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{memberGroupMap.get(get(record, source))}</span>);
}

MemberGroupField.defaultProps =
{
	label: '群組',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 管理員狀態顯示欄位元件。 */

const memberStatusMap = new Map<string, string>();
const memberStatusList: NamePairInfo<string>[] =
	[
		{ id: COMMON_THREE_STATUS.Enable, name: "啟用" },
		{ id: COMMON_THREE_STATUS.Disable, name: "停用" },
		{ id: COMMON_THREE_STATUS.Deleted, name: "已刪除" }
	];

for (let status of memberStatusList)
	memberStatusMap.set(status.id, status.name);

export const MemberStatusField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{memberStatusMap.get(get(record, source))}</span>);
}

MemberStatusField.defaultProps =
{
	label: '狀態',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 管理員列表頁面。 */
export const MemberList = (props: any) =>
{
	let { permissions } = usePermissions();
	let authInfo = permissions as MemberAuthInfo;

	return <List {...props} title='系統管理員' exporter={false}>
		<Datagrid bulkActionButtons={false} >
			<TextField source='id' />
			<MemberGroupField source='group' />
			<ReferenceArrayField label="權限" source="permissions" reference="memberPermission" sortable={false} >
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<TextField source='name' label='名稱' />
			<TextField source='email' />
			<MemberStatusField source='status' />
			<DateField source='createTime' label='建立時間' showTime />
			<ListEditButton edit={authInfo.memberEdit} />
			<ShowButton />
		</Datagrid>
	</List>
}

/** 管理員資訊頁面。 */
export const MemberShow = (props: any) =>
{
	let { permissions } = usePermissions();
	let authInfo = permissions as MemberAuthInfo;
	let opLogs: JSX.Element = <></>;

	if (authInfo.group <= MEMBER_GROUP.Manager && authInfo.memberCreate)
	{
		opLogs = <Tab label='操作記錄'>
			<ReferenceManyField reference='memberLog' target='ml_m_id' filter={{ ml_m_id: props.id }} pagination={<Pagination />} >
				<Datagrid bulkActionButtons={false}>
					<TextField source='id' />
					<ReferenceField label="操作者" source="memberId" reference="member">
						<TextField source="name" />
					</ReferenceField>
					<MemberLogTypeField label='執行動作' />
					<MemberLogDataField label='動作內容' />
					<DateField source='logTime' label='紀錄時間' showTime />
					<TextField source='remoteAddress' label='遠端位址' />
				</Datagrid>
			</ReferenceManyField>
		</Tab>
	}

	return <Show {...props}>
		<TabbedShowLayout>
			<Tab label='總覽'>
				<TextField source='id' />
				<MemberGroupField source='group' label='所屬群組' />
				<ReferenceArrayField label="權限" source="permissions" reference="memberPermission" sortable={false} >
					<SingleFieldList>
						<ChipField source="name" />
					</SingleFieldList>
				</ReferenceArrayField>
				<TextField source='name' label='名稱' />
				<TextField source='email' />
				<MemberStatusField source='status' />
				<DateField source='createTime' label='建立時間' showTime />
			</Tab>
			{opLogs}
		</TabbedShowLayout>
	</Show>;
}

/** 管理員編輯頁面。 */
export const MemberEdit = (props: any) => (
	<Edit {...props}>
		<SimpleForm>
			<Labeled label="ID"><TextField source='id' /></Labeled>
			<SelectInput source='group' choices={memberGroupList} label="所屬群組" />
			<Labeled label="權限">
				<ReferenceArrayInput source='permissions' reference='memberPermission' >
					<AutocompleteArrayInput optionText='name' />
				</ReferenceArrayInput>
			</Labeled>
			<TextInput source='name' label="名稱" />
			<TextInput source='email' type='email' label="email" />
			<PasswordInput source='passcode' label="密碼" />
			<SelectInput source='status' choices={memberStatusList} label="狀態" />
			<Labeled label="建立時間"><DateField source='createTime' showTime /></Labeled>
		</SimpleForm>
	</Edit>
);

/** 管理員建立頁面。 */
export const MemberCreate = (props: any) => (
	<Create {...props}>
		<SimpleForm>
			<SelectInput source='group' choices={memberGroupList} label="所屬群組" />
			<Labeled label="權限">
				<ReferenceArrayInput source='permissions' reference='memberPermission' >
					<AutocompleteArrayInput optionText='name' />
				</ReferenceArrayInput>
			</Labeled>
			<TextInput source='name' label="名稱" />
			<TextInput source='email' type='email' label="email" />
			<PasswordInput source='passcode' label="密碼" />
			<SelectInput source='status' choices={memberStatusList} label="狀態" />
		</SimpleForm>
	</Create>
);
